import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { useFormDraftPlainTools } from '@/hooks/index';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserService } from '@/api/types';

import api from '@/api';

import { SERVICE_CODE } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import {
  AddressSchema,
  ApplicantDataSchema,
  DisappearanceInfoSchema,
  ERDRInfoSchema,
  MissingPersonSchema,
  PersonalFeaturesSchema,
} from './model/schemas';
import { MissingPersonFormData, ServiceProps } from './model/types';
import { ApplicantPersonalData } from './ui/steps/ApplicantPersonalData';
import { DataPreview } from './ui/steps/DataPreview';
import { ERDRInformation } from './ui/steps/ERDRInfo';
import { MissingPersonDisappearanceInfo } from './ui/steps/InformationAboutDisappearance';
import { MissingPersonAddress } from './ui/steps/MissingPersonAddress';
import { MissingPersonPersonalData } from './ui/steps/MissingPersonPersonalData';
import { MissingPersonResult } from './ui/steps/MissingPersonResult';
import { PersonalFeatures } from './ui/steps/PersonalFeatures';

import './MissingPerson.scss';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Особисті дані зниклого',
    step: MissingPersonPersonalData,
    validationSchema: MissingPersonSchema,
  },
  { title: 'Прикмети зниклого', step: PersonalFeatures, validationSchema: PersonalFeaturesSchema },
  { title: 'Адреса зниклого', step: MissingPersonAddress, validationSchema: AddressSchema },
  {
    title: 'Інформація про зникнення',
    step: MissingPersonDisappearanceInfo,
    validationSchema: DisappearanceInfoSchema,
  },
  { title: 'Кримінальне провадження та ДНК', step: ERDRInformation, validationSchema: ERDRInfoSchema },
  { title: 'Особисті дані заявника', step: ApplicantPersonalData, validationSchema: ApplicantDataSchema },
  { title: 'Підтвердження даних', step: DataPreview },
  { title: 'Результат', step: MissingPersonResult },
];

export const MissingPerson: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;

  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);

  const { isLoading, currentRequestId, onDraftCreate, onDraftSave, onDraftContinue, onPaymentContinue } =
    useFormDraftPlainTools({
      serviceCode: SERVICE_CODE,
      setData: (data) => setDefaultValues(data ?? {}),
    });

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});

  const handleCreateDraft = useCallback(
    async (data?: { stateForm: FormDataType }, specificStep?: (step: number) => void) => {
      return (
        specificStep &&
        onDraftCreate(
          {
            id: currentService.id,
            name: currentService.name,
            code: SERVICE_CODE,
            totalSteps: STEPS.length,
            currentStep: 1,
            lastStepFilled: 0,
          },
          specificStep,
          data,
        )
      );
    },
    [onDraftCreate, currentService.id, currentService.name, currentService.code, STEPS.length],
  );

  const generatePDF = async (formData: MissingPersonFormData): Promise<void> => {
    const payload = await generatePayload({
      requestId: currentRequestId ?? '',
      formData,
      service: currentService,
      withAttachments: false,
    });

    const pdfArtifact = await api.services.generatePdf(currentService.code, payload);
    serviceFileManager.setAttachment(pdfArtifact);
  };

  const submitForm = async (formData: MissingPersonFormData): Promise<void> =>
    api.services
      .submit(
        currentService.code,
        await generatePayload({
          withAttachments: true,
          formData,
          requestId: currentRequestId ?? '',
          service: currentService,
          serviceFileManager,
        }),
        String(formData.allowProfileUpdate),
      )
      .then(() => {
        serviceFileManager.download();
        serviceFileManager.clear();
      });

  return (
    <ServiceFormTemplate<ServiceProps>
      className="missing-person-service"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={onDraftContinue}
      onPaymentContinue={onPaymentContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleCreateDraft}
      stepProps={{
        generatePDF,
        submitForm,
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
      }}
    />
  );
};
